<script setup lang="ts">
    import { CONTACT_TYPE } from '~/app/contact/enums/ContactType';
    import Contact from '~/app/contact/models/Contact';
    import { INVOICE_STATUS } from '~/app/invoicing/enums/InvoiceStatus';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';

    interface Props extends FormInputProps {
        withCreateButton?: boolean;
        contactType: typeof CONTACT_TYPE.CLIENT | typeof CONTACT_TYPE.SUPPLIER;
        openFromInvoiceForm?: boolean;
        disabled?: boolean;
        placeholder?: string;
    }

    const emit = defineEmits(['selected', 'change']);

    const select = ref(null);
    const props = withDefaults(defineProps<Props>(), {
        withCreateButton: false,
        openFromInvoiceForm: false,
        disabled: false,
    });
    const inputData: FormInputData = useFormInput(props);
    const displayCreateButton = computed(() => props.withCreateButton && !inputData?.model.value);

    useListen('contact:contact:updated', (contact) => {
        if (inputData.model.value && contact.is(inputData.model.value)) {
            emitChangeForDraftDocument(contact);
            inputData.model.value = contact;
        }
    });

    useListen('contact:contact:deleted', (uuid) => {
        if (inputData.model.value && inputData.model.value.getId() === uuid) {
            inputData.model.value = null;
        }
    });

    const emitChangeForDraftDocument = (contact: Contact | null): void => {
        if (props.form?.invoice && props.form?.invoice?.status !== INVOICE_STATUS.DRAFT) return;
        if (inputData.model.value?.locale !== contact?.locale) emit('change', contact);
    };

    const selectContact = (contact: Contact | null) => {
        emitChangeForDraftDocument(contact);
        inputData.model.value = contact;
        emit('selected', contact);
    };
</script>

<template>
    <FormElement
        :input-data="inputData"
        @label-clicked="select?.show()"
    >
        <SelectContact
            ref="select"
            :contact-type="contactType"
            :initial-value="inputData.model.value"
            :with-create-button="displayCreateButton"
            :disabled="disabled"
            :filled-by-ai="props.filledByAi"
            :placeholder="placeholder"
            @selected="selectContact"
            @unselect="inputData.model.value = null"
        >
            <template
                v-if="inputData.model.value"
                #button
            >
                <div
                    v-if="!disabled || !inputData.model.value.isEditable"
                    class="flex items-center h-full space-x-3"
                >
                    <a
                        href="#"
                        class="text-xs text-gray-600 transition-all cursor-pointer hover:text-primary"
                        :data-tooltip="$t('actions.edit')"
                        @click.stop.prevent="inputData.model.value.edit({ openFromInvoiceForm })"
                    >
                        <i class="fa-regular fa-pencil" />
                    </a>
                    <a
                        v-if="inputData.model.value?.isNotSnapshot()"
                        href="#"
                        class="text-sm text-gray-600 transition-all cursor-pointer hover:text-primary"
                        :data-tooltip="$t('actions.unselect')"
                        @click.stop.prevent="inputData.model.value = null"
                    >
                        <i class="fa-regular fa-xmark" />
                    </a>
                </div>
            </template>
        </SelectContact>
    </FormElement>
</template>
